/**
 * Lógica del armado del plano para VARIAS motos a la vez, sin React ni servicios adentro.
 *
 * Vive aparte del workbench para poder probarla sola: qué celda de la grilla existe, qué pisa el
 * «Aplicar a todas», qué se manda a guardar y qué moto frena el «Continuar». Todo eso es regla de
 * negocio —un margen mal puesto llega a SAP— y no debería depender de que alguien lo mire en pantalla.
 */

/** Las fórmulas que llevan margen. 'base', 'pvp' y 'fijo' no tienen margen que poner. */
export const FORMULAS_CON_MARGEN = ['margen', 'margen_flete', 'porcentaje']

/** Llave de una celda de la grilla: moto + clasificación. */
export const claveCelda = (grupoId, listaId) => `${Number(grupoId)}_${Number(listaId)}`

/** Cómo se nombra una moto en la grilla, en la tabla de líneas y en los avisos. */
export const etiquetaMoto = (g) => {
  if (!g) return ''
  const nombre = `${g.modelo || ''} ${g.anio_modelo || ''}`.trim()
  return nombre || g.conca || `#${g.id}`
}

/**
 * El precio con el que se arma el plano: la propuesta de Trade gana sobre el vigente, igual que en
 * sp_precios_plano_calcular. Cero = la moto no tiene con qué armar plano.
 */
export const pvpEfectivoDe = (g) => {
  if (!g) return 0
  return Number(g.pvp_propuesto) > 0 ? Number(g.pvp_propuesto) : Number(g.pvp || 0)
}

/**
 * Las condiciones de una moto que APLICAN y llevan margen. La fórmula sale del catálogo de listas,
 * igual que en el modo de una sola moto.
 */
export const margenesEditables = (margenes = [], listas = []) => {
  const conMargen = new Set(FORMULAS_CON_MARGEN)
  const formulaDe = (id) => {
    const l = (listas || []).find((x) => Number(x.id) === Number(id))
    return l ? l.formula : null
  }
  return (margenes || [])
    .filter((m) => Number(m.aplica) === 1 && conMargen.has(formulaDe(m.lista_id)))
    .sort((a, b) => String(a.tabla_sap).localeCompare(String(b.tabla_sap)))
}

/**
 * No resuelve NINGÚN margen: ni propio, ni heredado, ni general. Un margen cargado en 0 % no entra
 * acá: cero es una decisión, no un vacío.
 */
export const faltaMargen = (m) => Boolean(m) && (
  m.origen === 'sin_regla' || m.margen === null || m.margen === undefined || m.margen === ''
)

/**
 * El margen actual de la celda en %, con dos decimales, o null si no tiene. Con `sin_regla` el back
 * manda margen 0 (fn_precios_margen hace COALESCE a 0), pero ese 0 no es un margen cargado: se trata
 * como vacío. Si no, la celda se pintaba «0» y teclear 0 no contaba como cambio.
 */
export const pctActual = (m) => {
  if (!m || m.origen === 'sin_regla' || m.margen === null || m.margen === undefined || m.margen === '') return null
  return Number((Number(m.margen) * 100).toFixed(2))
}

/** Lee lo tecleado: vacío = null (no se tocó); si no, el número, aceptando la coma decimal. */
export const leerPct = (txt) => {
  const t = String(txt === null || txt === undefined ? '' : txt).trim()
  if (t === '') return null
  return Number(t.replace(',', '.'))
}

/** Mismo rango que el guardado de una sola moto: 0 a 99,99 %. */
export const esPctValido = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n < 100

/**
 * El % tecleado como fracción en texto, que es lo que guarda el back. Redondeado a 6 decimales:
 * 1,4 / 100 en coma flotante da 0.013999999999999999, y eso no es lo que nadie tecleó.
 */
export const fraccion = (num) => String(Number((Number(num) / 100).toFixed(6)))

/**
 * ¿Siguen elegidas las mismas motos? Compara por id, en orden. Sirve para descartar una recarga o
 * un cálculo que terminó después de que alguien cambió la selección.
 */
export const mismaSeleccion = (a = [], b = []) => {
  const x = a || []
  const y = b || []
  return x.length === y.length && x.every((g, i) => Number(g && g.id) === Number(y[i] && y[i].id))
}

/**
 * La grilla: una fila por moto, una columna por clasificación editable de CUALQUIERA de las motos.
 * Si en una moto esa clasificación no aplica (o no lleva margen), la celda no existe: se pinta
 * «no aplica» y nada la puede llenar.
 *
 * porMoto: [{ grupo, margenes }] tal como vuelve getGrupoMargenes para cada moto.
 */
export const construirGrilla = (porMoto = [], listas = []) => {
  const columnas = new Map()
  const filas = (porMoto || []).map(({ grupo, margenes }) => {
    const celdas = {}
    margenesEditables(margenes, listas).forEach((m) => {
      celdas[Number(m.lista_id)] = m
      if (!columnas.has(Number(m.lista_id))) {
        columnas.set(Number(m.lista_id), {
          lista_id: Number(m.lista_id),
          lista_label: m.lista_label,
          tabla_sap: m.tabla_sap,
          condicion: m.condicion,
        })
      }
    })
    return { grupo, celdas }
  })
  const orden = [...columnas.values()].sort((a, b) => (
    String(a.tabla_sap).localeCompare(String(b.tabla_sap))
    || String(a.lista_label || '').localeCompare(String(b.lista_label || ''))
  ))
  return { columnas: orden, filas }
}

/**
 * «Aplicar a todas»: pone el mismo % en esa columna para cada moto donde la clasificación aplica.
 * Con `soloVacias` solo llena las vacías: sin margen en el servidor Y sin nada tecleado todavía.
 * Las que ya tienen margen, o donde alguien ya escribió un % a mano, no se pisan.
 * No valida el número: eso lo hace quien llama, antes, para poder avisar.
 */
export const aplicarAColumna = (grilla, edits, listaId, txt, soloVacias = false) => {
  const sig = { ...(edits || {}) }
  let aplicadas = 0
  const filas = (grilla && grilla.filas) || []
  filas.forEach((f) => {
    const celda = f.celdas[Number(listaId)]
    if (!celda) return
    const clave = claveCelda(f.grupo.id, listaId)
    const tecleado = sig[clave] !== undefined && String(sig[clave]).trim() !== ''
    if (soloVacias && (!faltaMargen(celda) || tecleado)) return
    sig[clave] = String(txt)
    aplicadas += 1
  })
  return { edits: sig, aplicadas }
}

/**
 * De lo tecleado a lo que se manda al guardado masivo. Solo van las celdas que CAMBIAN: lo que se
 * dejó igual al margen actual no se escribe, porque cada escritura versiona y crearía una excepción
 * propia que nadie pidió. Una celda sin margen siempre cuenta como cambio.
 *
 * El valor va como fracción en texto, redondeada (12,5 % -> '0.125'; 1,4 % -> '0.014').
 */
export const cambiosAItems = (grilla, edits, notaDe = null) => {
  const items = []
  const invalidos = []
  const cols = (grilla && grilla.columnas) || []
  const filas = (grilla && grilla.filas) || []
  filas.forEach((f) => {
    cols.forEach((c) => {
      const celda = f.celdas[c.lista_id]
      if (!celda) return
      const txt = (edits || {})[claveCelda(f.grupo.id, c.lista_id)]
      if (txt === undefined) return
      const num = leerPct(txt)
      if (num === null) return
      if (!esPctValido(num)) {
        invalidos.push({ grupo: f.grupo, lista_id: c.lista_id, lista_label: c.lista_label, txt: String(txt) })
        return
      }
      const actual = pctActual(celda)
      if (!faltaMargen(celda) && actual !== null && Math.abs(actual - num) < 1e-9) return
      const item = {
        grupo_id: Number(f.grupo.id),
        lista_id: Number(c.lista_id),
        valor: fraccion(num),
      }
      if (notaDe) item.nota = notaDe(f.grupo)
      items.push(item)
    })
  })
  return { items, invalidos }
}

/** Tope de items por envío que acepta el back en el guardado masivo. */
export const MAX_ITEMS_POR_ENVIO = 2000

/** Parte los items en lotes de a `tam`: el back rechaza el envío entero si se pasa del tope. */
export const enLotes = (items = [], tam = MAX_ITEMS_POR_ENVIO) => {
  const lotes = []
  const todos = items || []
  for (let i = 0; i < todos.length; i += tam) lotes.push(todos.slice(i, i + tam))
  return lotes
}

/**
 * Lo que devolvió el guardado masivo, contado contra lo que se mandó. Si la llamada entera falla,
 * fallan todas; si falta la fila de una, esa cuenta como no guardada: no se asume éxito por silencio.
 *
 * Guardada = la fila vuelve SIN error. No se mira `result`: el back lo devuelve en 0 también en
 * casos válidos (quitar una excepción). El back omite las claves nulas, así que una clave que falta
 * vale lo mismo que null.
 */
export const resumirGuardado = (resp, items = []) => {
  const total = items.length
  if (!resp || !Array.isArray(resp.data)) {
    const error = (resp && (resp.error || resp.message)) || 'la petición falló'
    return {
      ok: 0,
      total,
      fallas: items.map((i) => ({ grupo_id: Number(i.grupo_id), lista_id: Number(i.lista_id), error })),
    }
  }
  const fallas = []
  let ok = 0
  items.forEach((i) => {
    const d = resp.data.find(
      (x) => Number(x.grupo_id) === Number(i.grupo_id) && Number(x.lista_id) === Number(i.lista_id),
    )
    const error = d && d.error !== null && d.error !== undefined ? String(d.error).trim() : ''
    if (d && error === '') {
      ok += 1
      return
    }
    fallas.push({
      grupo_id: Number(i.grupo_id),
      lista_id: Number(i.lista_id),
      error: d ? error : 'sin respuesta del servidor',
    })
  })
  return { ok, total, fallas }
}

/** Lo mismo que resumirGuardado, sumando varios envíos: [{ items, resp }]. */
export const resumirLotes = (lotes = []) => (lotes || []).reduce((acc, { items, resp }) => {
  const r = resumirGuardado(resp, items)
  return { ok: acc.ok + r.ok, total: acc.total + r.total, fallas: acc.fallas.concat(r.fallas) }
}, { ok: 0, total: 0, fallas: [] })

/**
 * Las motos que tienen alguna condición sin margen, con cuáles. Es lo que frena «Continuar».
 * Una moto cuyos márgenes no se pudieron leer (`fallo`) también frena: no saber no es tener.
 */
export const sinMargenPorMoto = (porMoto = [], listas = []) => (porMoto || [])
  .map(({ grupo, margenes, fallo }) => ({
    grupo,
    sinLeer: Boolean(fallo),
    faltan: margenesEditables(margenes, listas).filter(faltaMargen),
  }))
  .filter((x) => x.sinLeer || x.faltan.length > 0)

/**
 * Las llaves moto + tabla + condición cuyas líneas saldrían sin margen. Con varias motos la llave
 * lleva la moto: que a una le falte el 807 no frena el 807 de las otras.
 */
export const clavesSinMargenPorMoto = (porMoto = [], listas = []) => new Set(
  sinMargenPorMoto(porMoto, listas).flatMap(({ grupo, faltan }) => (
    faltan.map((m) => `${Number(grupo.id)}_${m.tabla_sap}_${m.condicion}`)
  )),
)

/**
 * Las líneas (ya marcadas con su moto) que no se pueden descargar: las de una condición sin margen
 * de esa moto, y todas las de una moto cuyos márgenes no se pudieron leer.
 */
export const lineasSinMargen = (lineas = [], porMoto = [], listas = []) => {
  const claves = clavesSinMargenPorMoto(porMoto, listas)
  const sinLeer = new Set(
    sinMargenPorMoto(porMoto, listas).filter((x) => x.sinLeer).map((x) => Number(x.grupo.id)),
  )
  return (lineas || []).filter((l) => sinLeer.has(Number(l.moto_grupo_id))
    || claves.has(`${Number(l.moto_grupo_id)}_${l.tabla_sap}_${l.condicion}`))
}

/**
 * Lo mismo con UNA moto, cuyas líneas no vienen marcadas: si sus márgenes no se pudieron leer
 * (`fallo`) no sale ninguna; si no, solo las de una condición sin margen.
 */
export const lineasSinMargenUna = (lineas = [], margenes = [], listas = [], fallo = false) => {
  if (fallo) return [...(lineas || [])]
  const claves = new Set(
    margenesEditables(margenes, listas).filter(faltaMargen).map((m) => `${m.tabla_sap}_${m.condicion}`),
  )
  return (lineas || []).filter((l) => claves.has(`${l.tabla_sap}_${l.condicion}`))
}

/** Cada línea sabe de qué moto es: la tabla la muestra y el bloqueo de descarga la usa. */
export const marcarLineas = (lineas = [], grupo = {}) => (lineas || []).map((l) => ({
  ...l,
  moto_grupo_id: Number(grupo.id),
  moto_conca: grupo.conca || '',
  moto_label: etiquetaMoto(grupo),
}))

/**
 * Las líneas separadas por fecha «válido desde» (col_D). El TXT se nombra con la fecha y la hoja del
 * XLSX la lleva en la cabecera: con motos de fechas distintas en un mismo archivo, el nombre y la
 * cabecera mentirían para una parte de las líneas. Se baja una tanda por fecha.
 */
export const partirPorFecha = (lineas = []) => {
  const mapa = new Map()
  const todas = lineas || []
  todas.forEach((l) => {
    const f = String(l.col_D === null || l.col_D === undefined ? '' : l.col_D).trim()
    if (!mapa.has(f)) mapa.set(f, [])
    mapa.get(f).push(l)
  })
  return [...mapa.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([fecha, ls]) => ({ fecha, lineas: ls }))
}

/**
 * Calcula el plano de varias motos, una detrás de otra. Secuencial a propósito: el procedimiento
 * trabaja por grupo. Si una falla, las otras siguen: se junta el motivo de cada una en vez de cortar.
 * La moto sin PVP ni propuesta ni se manda: se salta y se informa.
 *
 * `calcular` y `leerLineas` son calcularPlano y getPlanoLineas; llegan por parámetro para poder
 * probar la regla sin backend.
 */
export const calcularVarias = async (grupos, org, { calcular, leerLineas, onProgreso = null }) => {
  const lineas = []
  const resultados = []
  const lista = grupos || []
  // eslint-disable-next-line no-restricted-syntax
  for (const [i, g] of lista.entries()) {
    if (onProgreso) onProgreso(i, lista.length, g)
    if (pvpEfectivoDe(g) === 0) {
      resultados.push({ grupo: g, estado: 'saltada', error: 'no tiene PVP ni propuesta de Trade' })
      // eslint-disable-next-line no-continue
      continue
    }
    // eslint-disable-next-line no-await-in-loop
    const r = await calcular(g.id, org, 0)
    const fila = (r && r.data && r.data[0]) || null
    if (!r || (fila && fila.error)) {
      resultados.push({ grupo: g, estado: 'error', error: (fila && fila.error) || 'la petición falló' })
      // eslint-disable-next-line no-continue
      continue
    }
    // eslint-disable-next-line no-await-in-loop
    const ls = await leerLineas(g.id, 0)
    if (!ls) {
      resultados.push({ grupo: g, estado: 'error', fila, error: 'se calculó pero no se pudieron leer las líneas' })
      // eslint-disable-next-line no-continue
      continue
    }
    const propias = marcarLineas(ls.data || [], g)
    propias.forEach((l) => lineas.push(l))
    resultados.push({ grupo: g, estado: 'ok', fila, lineas: propias.length })
  }
  if (onProgreso) onProgreso(lista.length, lista.length, null)
  return { lineas, resultados }
}
